import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useNavigate} from 'react-router-dom'
import {useAppSelector} from '~/src/store/store-hooks'
import {useSignoutMutation} from '~/src/store/apis/auth-api'

// =============================================================================
// SESSION CONFIGURATION
// =============================================================================
// Session duration: 10 hours (enforced on frontend)
// This covers a full workday (8 hours) plus buffer, while reducing security risk
const SESSION_DURATION_MS = 10 * 60 * 60 * 1000 // 10 hours in milliseconds

// Warning threshold: Show notification 15 minutes before session expires
const WARNING_THRESHOLD_MS = 15 * 60 * 1000 // 15 minutes

// Check interval: How often to check session status
const CHECK_INTERVAL_MS = 60 * 1000 // Every 60 seconds

// Debug mode: Set to true for console logs (disable in production)
const DEBUG_SESSION = false

// LocalStorage/SessionStorage keys
export const SESSION_EXPIRED_KEY = 'session_expired_reason'
export const SESSION_LOGIN_TIME_KEY = 'session_login_time'

// Debug logger
const debugLog = (message: string, data?: unknown) => {
  if (DEBUG_SESSION) {
    const timestamp = new Date().toLocaleTimeString()
    const prefix = `🔒 [SessionTimeout ${timestamp}]`
    if (data !== undefined) {
      console.warn(prefix, message, data)
    } else {
      console.warn(prefix, message)
    }
  }
}

/**
 * Set the session login time (call this on successful login)
 */
export function setSessionLoginTime(): void {
  const loginTime = Date.now().toString()
  localStorage.setItem(SESSION_LOGIN_TIME_KEY, loginTime)
  debugLog('Session login time set', {
    loginTime: new Date(parseInt(loginTime, 10)).toLocaleString(),
  })
}

/**
 * Get the session expiration time based on login time + 10 hours
 */
function getSessionExpiration(): number | null {
  const loginTimeStr = localStorage.getItem(SESSION_LOGIN_TIME_KEY)
  if (!loginTimeStr) {
    debugLog('getSessionExpiration: No login time found')
    return null
  }

  const loginTime = parseInt(loginTimeStr, 10)
  if (isNaN(loginTime)) {
    debugLog('getSessionExpiration: Invalid login time', loginTimeStr)
    return null
  }

  const expirationTime = loginTime + SESSION_DURATION_MS
  debugLog('getSessionExpiration: Session expires at', {
    loginTime: new Date(loginTime).toLocaleString(),
    expirationTime: new Date(expirationTime).toLocaleString(),
    minutesRemaining: Math.ceil((expirationTime - Date.now()) / 60000),
  })

  return expirationTime
}

/**
 * Clear the session login time (call this on logout)
 */
export function clearSessionLoginTime(): void {
  localStorage.removeItem(SESSION_LOGIN_TIME_KEY)
  debugLog('Session login time cleared')
}

interface SessionTimeoutContextType {
  /** Whether the warning notification should be shown */
  showWarning: boolean
  /** Minutes remaining until session expires */
  minutesRemaining: number
  /** Dismiss the warning (user acknowledged but didn't extend) */
  dismissWarning: () => void
  /** Extend the session / stay signed in */
  staySignedIn: () => void
  /** Manually trigger logout */
  logout: () => void
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined)

interface SessionTimeoutProviderProps {
  children: React.ReactNode
}

/**
 * Provider that manages session timeout warnings and auto-logout.
 *
 * Features:
 * - Enforces 10-hour session duration on the frontend
 * - Shows warning 15 minutes before session expires
 * - Allows user to dismiss warning
 * - Auto-logs out when 10 hours have passed since login
 * - Stores logout reason for post-logout message
 */
export function SessionTimeoutProvider({children}: SessionTimeoutProviderProps) {
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.root.token)
  const onSignIn = useAppSelector((state) => state.root.onSignIn)
  const [signout] = useSignoutMutation()

  debugLog('Provider mounted', {onSignIn, hasToken: !!token})

  const [showWarning, setShowWarning] = useState(false)
  const [minutesRemaining, setMinutesRemaining] = useState(0)
  const [warningShown, setWarningShown] = useState(false)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const sessionExpRef = useRef<number | null>(null)

  /**
   * Perform logout and redirect to signin
   */
  const performLogout = useCallback(
    async (reason: 'expired' | 'manual') => {
      debugLog(`performLogout called with reason: ${reason}`)

      // Store reason for post-logout message
      if (reason === 'expired') {
        sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true')
        debugLog('Set SESSION_EXPIRED_KEY in sessionStorage')
      }

      // Clear interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      // Clear session login time
      clearSessionLoginTime()

      // Perform signout
      try {
        const email = localStorage.getItem('email')
        if (email) {
          debugLog('Calling signout API...')
          await signout({email: JSON.parse(email)}).unwrap()
          debugLog('Signout API successful')
        } else {
          debugLog('No email found, clearing localStorage')
          window.localStorage.clear()
        }
      } catch (error) {
        debugLog('Signout API failed, clearing localStorage anyway', error)
        window.localStorage.clear()
      }

      // Reset state
      setShowWarning(false)
      setWarningShown(false)

      // Redirect to signin
      debugLog('Redirecting to /signin')
      navigate('/signin', {replace: true})
    },
    [signout, navigate],
  )

  /**
   * Check session status and update warning state
   */
  const checkSession = useCallback(() => {
    const expMs = sessionExpRef.current

    if (!expMs) {
      debugLog('checkSession: No expiration time set')
      setShowWarning(false)
      return
    }

    const now = Date.now()
    const timeUntilExpMs = expMs - now
    const minutes = Math.ceil(timeUntilExpMs / 60000)
    const thresholdMinutes = WARNING_THRESHOLD_MS / 60000

    debugLog(`checkSession: ${minutes} min remaining (threshold: ${thresholdMinutes} min)`, {
      expiresAt: new Date(expMs).toLocaleTimeString(),
      warningShown,
    })

    // Already expired - auto logout
    if (timeUntilExpMs <= 0) {
      debugLog('⚠️ SESSION EXPIRED (10 hours reached)! Logging out...')
      void performLogout('expired')
      return
    }

    setMinutesRemaining(minutes)

    // Show warning if within threshold and not already shown
    if (timeUntilExpMs <= WARNING_THRESHOLD_MS && !warningShown) {
      debugLog('⚠️ SHOWING WARNING: Session expiring soon!')
      setShowWarning(true)
      setWarningShown(true)
    }
  }, [warningShown, performLogout])

  /**
   * Dismiss warning without extending session
   */
  const dismissWarning = useCallback(() => {
    debugLog('dismissWarning called - hiding warning')
    setShowWarning(false)
  }, [])

  /**
   * Stay signed in - currently just dismisses warning
   * TODO: Re-enable when BE refresh endpoint is available
   */
  const staySignedIn = useCallback(() => {
    debugLog('staySignedIn called - dismissing warning')
    setShowWarning(false)
  }, [])

  /**
   * Manual logout
   */
  const logout = useCallback(() => {
    void performLogout('manual')
  }, [performLogout])

  // Update session expiration ref when user signs in
  useEffect(() => {
    if (onSignIn && token) {
      // Set login time if not already set (first login or page refresh while logged in)
      if (!localStorage.getItem(SESSION_LOGIN_TIME_KEY)) {
        setSessionLoginTime()
      }
      sessionExpRef.current = getSessionExpiration()
      debugLog('Session expiration ref updated', {
        hasExpiration: !!sessionExpRef.current,
        expiresAt: sessionExpRef.current ? new Date(sessionExpRef.current).toLocaleString() : null,
      })
    }
  }, [onSignIn, token])

  // Set up interval to check session status
  useEffect(() => {
    debugLog('useEffect triggered', {onSignIn, hasToken: !!token})

    // Only run if user is signed in and has a token
    if (!onSignIn || !token) {
      debugLog('User not signed in or no token - clearing interval')
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setShowWarning(false)
      setWarningShown(false)
      return
    }

    debugLog(`Starting session monitoring (checking every ${CHECK_INTERVAL_MS / 1000}s)`)

    // Initial check
    checkSession()

    // Set up interval
    intervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS)

    return () => {
      debugLog('Cleaning up interval')
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [onSignIn, token, checkSession])

  const contextValue: SessionTimeoutContextType = useMemo(
    () => ({
      showWarning,
      minutesRemaining,
      dismissWarning,
      staySignedIn,
      logout,
    }),
    [showWarning, minutesRemaining, dismissWarning, staySignedIn, logout],
  )

  return (
    <SessionTimeoutContext.Provider value={contextValue}>{children}</SessionTimeoutContext.Provider>
  )
}

/**
 * Hook to access session timeout state and actions
 */
export function useSessionTimeout(): SessionTimeoutContextType {
  const context = useContext(SessionTimeoutContext)

  if (!context) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider')
  }

  return context
}

// Export configuration for reference
export {SESSION_DURATION_MS, WARNING_THRESHOLD_MS}
